import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Crosshair, Skull, Bug, Bird, ShieldAlert, Zap, AlertTriangle, Target, Activity, ChevronRight } from 'lucide-react';
import { ITEMS } from '@/config/constants';
import LeonDialogue from '@/components/LeonDialogue';
import { SHOOTING_GALLERY } from '@/config/missionContent';

const TARGET_TYPES = [
  { type: 'zombie', Icon: Skull, points: 1, color: '#ef4444', size: 56, duration: 2200, label: 'ZOMBIE' },
  { type: 'licker', Icon: Bug, points: 2, color: '#a855f7', size: 48, duration: 1600, label: 'LICKER' },
  { type: 'crow', Icon: Bird, points: 1, color: '#6b7280', size: 40, duration: 1800, label: 'CROW' },
  { type: 'tyrant', Icon: ShieldAlert, points: 3, color: '#dc2626', size: 68, duration: 2800, label: 'TYRANT' },
];

const SCORE_GOAL = 48;
const TOTAL_SPAWNS = 50;
const MAX_CONCURRENT = 5;

// ─── Background ───
function RangeBackground() {
  const particles = useMemo(() =>
    Array.from({ length: 12 }, (_, i) => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: i < 3 ? 3 : 1.5 + Math.random(),
      duration: 8 + Math.random() * 8,
      delay: Math.random() * 4,
      drift: (Math.random() - 0.5) * 12,
      isOrange: i < 3,
    })), []);

  return (
    <>
      {/* Base gradient */}
      <div className="fixed inset-0 bg-gradient-to-b from-black via-[#0a0205] to-black" />
      {/* Red grid */}
      <div className="fixed inset-0 opacity-[0.025]"
        style={{ backgroundImage: 'repeating-linear-gradient(0deg, rgba(239,68,68,0.5) 0px, transparent 1px, transparent 50px), repeating-linear-gradient(90deg, rgba(239,68,68,0.5) 0px, transparent 1px, transparent 50px)' }} />
      {/* Floating particles */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {particles.map((p, i) => (
          <motion.div
            key={i}
            className={`absolute rounded-full ${p.isOrange ? 'bg-orange-500/[0.06]' : 'bg-red-500/[0.04]'}`}
            style={{ width: p.size, height: p.size, left: `${p.x}%`, top: `${p.y}%` }}
            animate={{ y: [0, -18, 0], x: [0, p.drift, 0], opacity: [0.15, 0.4, 0.15] }}
            transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: 'easeInOut' }}
          />
        ))}
      </div>
      {/* Red vignette pulse */}
      <div className="fixed inset-0 pointer-events-none animate-pulse"
        style={{ animationDuration: '5s', boxShadow: 'inset 0 0 80px rgba(180,0,0,0.08), inset 0 0 25px rgba(180,0,0,0.04)' }} />
      {/* Scanlines */}
      <div className="fixed inset-0 pointer-events-none z-40 opacity-[0.02]"
        style={{ background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(239,68,68,0.4) 2px, rgba(239,68,68,0.4) 4px)' }} />
    </>
  );
}

// ─── Kill Feed ───
function KillFeed({ kills }) {
  return (
    <div className="absolute top-2 right-2 z-20 space-y-1 w-36 pointer-events-none">
      <AnimatePresence>
        {kills.slice(-4).map(kill => (
          <motion.div
            key={kill.id}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="flex items-center gap-1.5 bg-black/70 border border-red-900/30 rounded-sm px-2 py-1 backdrop-blur-sm"
          >
            <kill.Icon size={10} style={{ color: kill.color }} />
            <span className="text-xs font-terminal tracking-wider text-red-400/80 flex-1">{kill.label}</span>
            <span className="text-xs font-orbitron text-yellow-400">+{kill.points}</span>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

// ─── Tactical HUD ───
function TacticalHUD({ score, spawned, active, combo }) {
  const threatLevel = spawned > 15 ? 'CRITICAL' : spawned > 8 ? 'HIGH' : 'MODERATE';
  const threatColor = spawned > 15 ? 'text-red-400' : spawned > 8 ? 'text-yellow-400' : 'text-green-400';
  const isCritical = spawned > 15;
  const scorePercent = Math.min((score / SCORE_GOAL) * 100, 100);
  const nearGoal = score >= 12;
  const atGoal = score >= SCORE_GOAL;

  return (
    <>
      {/* Top bar */}
      <div className="flex items-center justify-between mb-3 max-w-2xl mx-auto">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <Target size={12} className="text-red-500" />
            <span className="text-xs font-terminal text-red-500/80 tracking-wider">{SHOOTING_GALLERY.hud.firingRange}</span>
          </div>
          {active && (
            <div className={`flex items-center gap-1.5 ${isCritical ? 'animate-pulse' : ''}`}>
              <AlertTriangle size={10} className={threatColor} />
              <span className={`text-xs font-terminal tracking-wider ${threatColor}`}>{SHOOTING_GALLERY.hud.threat} {threatLevel}</span>
            </div>
          )}
        </div>
        <AnimatePresence>
          {combo > 1 && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: combo >= 5 ? 1.15 : 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              className="flex items-center gap-1.5 bg-yellow-950/30 border border-yellow-600/30 px-2 py-0.5 rounded"
              style={combo >= 5 ? { boxShadow: '0 0 10px rgba(234,179,8,0.2)' } : undefined}
            >
              <Zap size={combo >= 5 ? 12 : 10} className="text-yellow-400" />
              <span className={`font-orbitron text-yellow-400 ${combo >= 5 ? 'text-xs' : 'text-xs'}`}>
                {combo}x {SHOOTING_GALLERY.hud.combo.replace('x ', '')}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Score panel */}
      <div className="max-w-2xl mx-auto mb-3">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-terminal text-green-700/80 tracking-wider">{SHOOTING_GALLERY.hud.score}</span>
              <span className="font-orbitron text-sm text-green-400">{score}<span className="text-green-700/60">/{SCORE_GOAL}</span></span>
            </div>
            <div className="h-2.5 bg-black/60 rounded-sm border border-green-900/30 overflow-hidden relative">
              <div
                className="h-full rounded-sm relative transition-all duration-300 ease-out"
                style={{
                  width: `${scorePercent}%`,
                  background: 'linear-gradient(90deg, #dc2626 0%, #eab308 50%, #22c55e 100%)',
                  boxShadow: atGoal
                    ? '0 0 12px rgba(0,255,65,0.5)'
                    : nearGoal
                      ? '0 0 6px rgba(234,179,8,0.3)'
                      : 'none',
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent" />
              </div>
              {/* Notch marks */}
              {[25, 50, 75].map(pct => (
                <div key={pct} className="absolute top-0 bottom-0 w-px bg-white/10" style={{ left: `${pct}%` }} />
              ))}
            </div>
          </div>
          <div className="w-24 text-right">
             <span className="text-xs font-terminal text-green-700/80 tracking-wider block mb-1">{SHOOTING_GALLERY.hud.targets}</span>
            <span className="font-orbitron text-sm text-yellow-400">{spawned}<span className="text-yellow-700/60">/{TOTAL_SPAWNS}</span></span>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Enhanced Scope Corners ───
function ScopeCorners() {
  return (
    <>
      {[
        { pos: 'top-2 left-2', h: 'top-0 left-0 w-full h-[2px]', v: 'top-0 left-0 w-[2px] h-full' },
        { pos: 'top-2 right-2', h: 'top-0 right-0 w-full h-[2px]', v: 'top-0 right-0 w-[2px] h-full' },
        { pos: 'bottom-2 left-2', h: 'bottom-0 left-0 w-full h-[2px]', v: 'bottom-0 left-0 w-[2px] h-full' },
        { pos: 'bottom-2 right-2', h: 'bottom-0 right-0 w-full h-[2px]', v: 'bottom-0 right-0 w-[2px] h-full' },
      ].map((corner, i) => (
        <div key={i} className={`absolute ${corner.pos} w-12 h-12 pointer-events-none`}>
          <div className={`absolute ${corner.h} bg-red-500/30`} />
          <div className={`absolute ${corner.v} bg-red-500/30`} />
        </div>
      ))}
    </>
  );
}

// ─── Muzzle Flash (two-layer) ───
function MuzzleFlash({ flashes }) {
  return (
    <AnimatePresence>
      {flashes.map(f => (
        <motion.div
          key={f.id}
          className="absolute pointer-events-none z-20"
          style={{ left: `${f.x}%`, top: `${f.y}%` }}
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Inner flash */}
          <motion.div
            className="absolute -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.6), rgba(234,179,8,0.4), transparent)', boxShadow: '0 0 10px rgba(255,255,200,0.5)' }}
            initial={{ scale: 1 }}
            animate={{ scale: 0, opacity: 0 }}
            transition={{ duration: 0.1 }}
          />
          {/* Outer ring */}
          <motion.div
            className="absolute -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full border border-orange-400/40"
            initial={{ scale: 0.5, opacity: 0.6 }}
            animate={{ scale: 2.5, opacity: 0 }}
            transition={{ duration: 0.25 }}
          />
        </motion.div>
      ))}
    </AnimatePresence>
  );
}

// ─── Hit Sparks ───
function HitSparks({ sparks }) {
  return (
    <AnimatePresence>
      {sparks.map(spark => (
        <motion.div
          key={spark.id}
          className="absolute pointer-events-none z-20"
          style={{ left: `${spark.x}%`, top: `${spark.y}%` }}
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
        >
          {spark.particles.map((p, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 rounded-full bg-yellow-400"
              style={{ boxShadow: '0 0 3px rgba(234,179,8,0.6)' }}
              initial={{ x: 0, y: 0 }}
              animate={{ x: p.dx, y: p.dy, opacity: 0 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
            />
          ))}
        </motion.div>
      ))}
    </AnimatePresence>
  );
}

export default function ShootingGallery({ onComplete, onItemCollected }) {
  const [showLeon, setShowLeon] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [active, setActive] = useState(false);
  const [score, setScore] = useState(0);
  const [targets, setTargets] = useState([]);
  const [hits, setHits] = useState([]);
  const [spawned, setSpawned] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [showFinalLeon, setShowFinalLeon] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [kills, setKills] = useState([]);
  const [combo, setCombo] = useState(0);
  const [flashes, setFlashes] = useState([]);
  const [sparks, setSparks] = useState([]);
  const [shakeKey, setShakeKey] = useState(0);
  const [currentTime, setCurrentTime] = useState('');
  const [successPhase, setSuccessPhase] = useState(0);
  const [showProceed, setShowProceed] = useState(false);
  const nextId = useRef(0);
  const spawnTimer = useRef(null);
  const comboTimer = useRef(null);

  // Mount-reset
  useEffect(() => {
    setShowLeon(true);
    setShowTutorial(false);
    setActive(false);
    setScore(0);
    setTargets([]);
    setHits([]);
    setSpawned(0);
    setGameOver(false);
    setShowFinalLeon(false);
    setShowSuccess(false);
    setKills([]);
    setCombo(0);
    setFlashes([]);
    setSparks([]);
    setShakeKey(0);
    setSuccessPhase(0);
    setShowProceed(false);
    nextId.current = 0;
    if (spawnTimer.current) clearInterval(spawnTimer.current);
    if (comboTimer.current) clearTimeout(comboTimer.current);
  }, []);

  // Live clock
  useEffect(() => {
    const update = () => setCurrentTime(new Date().toLocaleTimeString('en-US', { hour12: false }));
    update();
    const i = setInterval(update, 1000);
    return () => clearInterval(i);
  }, []);

  const handleLeonDismiss = useCallback(() => {
    setShowLeon(false);
    setShowTutorial(true);
  }, []);

  const handleStartShooting = useCallback(() => {
    setShowTutorial(false);
    setActive(true);
  }, []);

  // Spawn targets
  useEffect(() => {
    if (!active || gameOver) return;
    const spawnTarget = () => {
      if (spawned >= TOTAL_SPAWNS) return;
      setTargets(prev => {
        if (prev.length >= MAX_CONCURRENT) return prev;
        const tType = TARGET_TYPES[Math.floor(Math.random() * TARGET_TYPES.length)];
        const id = nextId.current++;
        const x = 8 + Math.random() * 72;
        const y = 8 + Math.random() * 62;
        const target = { ...tType, id, x, y };
        setSpawned(p => p + 1);
        setTimeout(() => setTargets(t => t.filter(tt => tt.id !== id)), tType.duration);
        return [...prev, target];
      });
    };
    spawnTarget();
    spawnTimer.current = setInterval(spawnTarget, 900);
    return () => clearInterval(spawnTimer.current);
  }, [active, gameOver, spawned]);

  // Check game end
  useEffect(() => {
    if (!active) return;
    if (score >= SCORE_GOAL) {
      setGameOver(true);
      setActive(false);
      clearInterval(spawnTimer.current);
      setShowSuccess(true);
      setSuccessPhase(1);
      setTimeout(() => setSuccessPhase(2), 500);
      setTimeout(() => setSuccessPhase(3), 1200);
      setTimeout(() => setShowFinalLeon(true), 2000);
    } else if (spawned >= TOTAL_SPAWNS && targets.length === 0 && score < SCORE_GOAL) {
      setSpawned(0);
      nextId.current = 0;
    }
  }, [active, score, spawned, targets.length]);

  const handleShoot = useCallback((target, e) => {
    e.stopPropagation();
    if (!active) return;
    setScore(prev => prev + target.points);
    setHits(prev => [...prev, { id: target.id, x: target.x, y: target.y, points: target.points }]);
    setTargets(prev => prev.filter(t => t.id !== target.id));
    setKills(prev => [...prev.slice(-6), { ...target }]);
    setFlashes(prev => [...prev, { id: Date.now(), x: target.x, y: target.y }]);

    // Hit sparks — pre-compute particle trajectories
    const sparkParticles = Array.from({ length: 5 }, () => ({
      dx: (Math.random() - 0.5) * 40,
      dy: (Math.random() - 0.5) * 40,
    }));
    setSparks(prev => [...prev, { id: Date.now() + Math.random(), x: target.x, y: target.y, particles: sparkParticles }]);

    // Screen shake — stronger for tyrant
    setShakeKey(prev => prev + 1);

    // Combo
    clearTimeout(comboTimer.current);
    setCombo(prev => prev + 1);
    comboTimer.current = setTimeout(() => setCombo(0), 1800);

    setTimeout(() => setHits(prev => prev.filter(h => h.id !== target.id)), 700);
    setTimeout(() => setFlashes(prev => prev.slice(1)), 200);
    setTimeout(() => setSparks(prev => prev.slice(1)), 500);
  }, [active]);

  const handleMiss = useCallback(() => {
    if (!active) return;
    setCombo(0);
  }, [active]);

  const handleFinalDismiss = useCallback(() => {
    setShowFinalLeon(false);
    onItemCollected?.(ITEMS.TACTICAL_SCOPE);
    setShowProceed(true);
  }, [onItemCollected]);

  const comboGlow = combo >= 5
    ? '0 0 25px rgba(234,179,8,0.25)'
    : combo >= 3
      ? '0 0 15px rgba(234,179,8,0.12)'
      : undefined;

  return (
    <div className="puzzle-container relative overflow-hidden" data-testid="puzzle-shooting-gallery" style={{ cursor: active ? 'crosshair' : 'default' }}>
      <RangeBackground />

      {/* Screen shake wrapper */}
      <motion.div
        key={shakeKey}
        className="relative z-10"
        animate={shakeKey > 0 ? { x: [0, -3, 3, -2, 2, 0] } : {}}
        transition={{ duration: 0.15 }}
      >
        {/* Header */}
        <div className="text-center mb-4 relative z-10">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="w-8 h-px bg-gradient-to-r from-transparent to-red-700/30" />
            <Crosshair size={12} className="text-red-500/80" />
            <p className="text-xs font-terminal tracking-[6px] text-red-500/80 uppercase">{SHOOTING_GALLERY.checkpoint}</p>
            <Crosshair size={12} className="text-red-500/80" />
            <div className="w-8 h-px bg-gradient-to-l from-transparent to-red-700/30" />
          </div>
          <h2 className="font-orbitron text-2xl tracking-wider text-gray-200 uppercase">{SHOOTING_GALLERY.title}</h2>
          <p className="text-sm text-green-700/70 font-terminal mt-1">{SHOOTING_GALLERY.subtitle}</p>
        </div>

        {/* Tutorial Screen */}
        {showTutorial && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-lg mx-auto relative z-10"
          >
            <div className="bg-black/50 border border-red-800/30 rounded-lg p-6 space-y-5 backdrop-blur-sm"
              style={{ boxShadow: '0 0 20px rgba(0,0,0,0.4), inset 0 0 20px rgba(0,0,0,0.2)' }}>

              {/* Title */}
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Crosshair size={14} className="text-red-500/70" />
                  <span className="font-terminal text-xs tracking-[4px] text-red-500/70 uppercase">{SHOOTING_GALLERY.tutorial.heading}</span>
                  <Crosshair size={14} className="text-red-500/70" />
                </div>
                <p className="text-gray-400/80 font-rajdhani text-sm">
                  {SHOOTING_GALLERY.tutorial.body}
                </p>
              </div>

              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-red-800/20 to-transparent" />

              {/* Target types */}
              <div>
                <p className="font-terminal text-xs tracking-[3px] text-green-700/60 uppercase mb-3">{SHOOTING_GALLERY.tutorial.hostileTypes}</p>
                <div className="space-y-2.5">
                  {TARGET_TYPES.map(t => (
                    <motion.div
                      key={t.type}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: TARGET_TYPES.indexOf(t) * 0.1 }}
                      className="flex items-center gap-3 bg-black/30 rounded px-3 py-2 border border-gray-800/20"
                    >
                      {/* Icon */}
                      <div className="w-10 h-10 rounded-full border flex items-center justify-center flex-shrink-0"
                        style={{
                          borderColor: t.color,
                          background: `radial-gradient(circle, ${t.color}15, transparent)`,
                          boxShadow: `0 0 10px ${t.color}20`,
                        }}>
                        <t.Icon size={18} style={{ color: t.color }} />
                      </div>
                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-terminal text-xs tracking-wider" style={{ color: t.color }}>{t.label}</span>
                          <span className="font-orbitron text-sm text-yellow-400">+{t.points} {t.points === 1 ? 'pt' : 'pts'}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs font-terminal text-gray-500/70">WINDOW: {(t.duration / 1000).toFixed(1)}s</span>
                          <span className="text-xs font-terminal text-gray-600/50">•</span>
                          <span className="text-xs font-terminal text-gray-500/70">SIZE: {t.size > 60 ? 'LARGE' : t.size > 45 ? 'MEDIUM' : 'SMALL'}</span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-red-800/20 to-transparent" />

              {/* Rules */}
              <div className="space-y-1.5">
                <p className="font-terminal text-xs tracking-[3px] text-green-700/60 uppercase mb-2">{SHOOTING_GALLERY.tutorial.rules}</p>
                {[
                  { label: 'TARGET SCORE', value: `${SCORE_GOAL} points to clear the range` },
                  { label: 'COMBO', value: 'Hit targets in succession for a combo streak' },
                  { label: 'MAX TARGETS', value: `Up to ${MAX_CONCURRENT} hostiles on screen at once` },
                ].map((rule, i) => (
                  <motion.div
                    key={rule.label}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 + i * 0.1 }}
                    className="flex items-start gap-2"
                  >
                    <ChevronRight size={10} className="text-red-700/40 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-gray-400/70 font-rajdhani">
                      <span className="text-green-500/60 font-terminal text-xs tracking-wider mr-1.5">{rule.label}:</span>
                      {rule.value}
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* Start button */}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="text-center pt-2"
              >
                <motion.button
                  whileHover={{ scale: 1.03, boxShadow: '0 0 25px rgba(220,38,38,0.25)' }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleStartShooting}
                  className="group relative px-8 py-3 bg-gradient-to-r from-red-950/50 via-red-900/40 to-red-950/50 border-2 border-red-600/50 text-red-300 font-terminal text-base tracking-[5px] uppercase transition-all hover:border-red-500/70 overflow-hidden"
                >
                  {/* Shimmer */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-red-500/8 to-transparent pointer-events-none"
                    animate={{ x: ['-200%', '200%'] }}
                    transition={{ duration: 3, repeat: Infinity, repeatDelay: 1 }}
                  />
                  {/* Border pulse */}
                  <motion.div
                    className="absolute inset-0 pointer-events-none"
                    style={{ boxShadow: 'inset 0 0 0 2px rgba(220,38,38,0)' }}
                    animate={{ boxShadow: ['inset 0 0 0 2px rgba(220,38,38,0)', 'inset 0 0 0 2px rgba(220,38,38,0.3)', 'inset 0 0 0 2px rgba(220,38,38,0)'] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                  <span className="relative z-10 flex items-center justify-center gap-2.5">
                    <Crosshair size={16} />
                    {SHOOTING_GALLERY.tutorial.startButton}
                  </span>
                </motion.button>
              </motion.div>
            </div>
          </motion.div>
        )}

        {(active || gameOver) && (
          <div className="relative z-10">
            <TacticalHUD score={score} spawned={spawned} active={active} combo={combo} />

            {/* Shooting area */}
            <div
              className="relative mx-auto rounded border border-red-800/40 bg-black/60 overflow-hidden"
              style={{
                width: '100%', maxWidth: 680, height: 400,
                boxShadow: comboGlow || '0 0 15px rgba(220,38,38,0.08), inset 0 0 25px rgba(0,0,0,0.4)',
                transition: 'box-shadow 0.3s',
              }}
              onClick={handleMiss}
              data-testid="shooting-area"
            >
              <ScopeCorners />

              {/* Inner grid overlay */}
              <div className="absolute inset-0 opacity-[0.03]"
                style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 39px, rgba(239,68,68,0.3) 40px), repeating-linear-gradient(90deg, transparent, transparent 39px, rgba(239,68,68,0.3) 40px)' }} />

              {/* Horizontal scan line */}
              {active && (
                <motion.div
                  animate={{ top: ['0%', '100%'] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                  className="absolute left-0 right-0 h-px bg-red-600/8 pointer-events-none z-10"
                />
              )}

              {/* Center crosshair */}
              {active && (
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-[5]">
                  <div className="relative w-16 h-16 opacity-[0.07]">
                    <div className="absolute top-0 left-1/2 w-px h-5 bg-red-500 -translate-x-1/2" />
                    <div className="absolute bottom-0 left-1/2 w-px h-5 bg-red-500 -translate-x-1/2" />
                    <div className="absolute left-0 top-1/2 w-5 h-px bg-red-500 -translate-y-1/2" />
                    <div className="absolute right-0 top-1/2 w-5 h-px bg-red-500 -translate-y-1/2" />
                    <div className="absolute inset-3 border border-red-500/30 rounded-full" />
                  </div>
                </div>
              )}

              <KillFeed kills={kills} />
              <MuzzleFlash flashes={flashes} />
              <HitSparks sparks={sparks} />

              {/* Targets */}
              <AnimatePresence>
                {targets.map(target => {
                  const TIcon = target.Icon;
                  return (
                    <motion.div
                      key={target.id}
                      initial={{ scale: 0, opacity: 0, rotate: -10 }}
                      animate={{ scale: 1, opacity: 1, rotate: 0, y: [0, -3, 0] }}
                      exit={{ scale: [1.15, 0], opacity: 0, filter: 'blur(4px)' }}
                      transition={{
                        default: { duration: 0.2, type: 'spring', stiffness: 400, damping: 12 },
                        y: { duration: 2, repeat: Infinity, ease: 'easeInOut' },
                        exit: { duration: 0.25 },
                      }}
                      onClick={(e) => handleShoot(target, e)}
                      className="absolute flex flex-col items-center gap-1 cursor-crosshair group z-10"
                      style={{ left: `${target.x}%`, top: `${target.y}%` }}
                      data-testid={`target-${target.id}`}
                    >
                      {/* Danger ring */}
                      <motion.div
                        animate={{ scale: [1, 1.3, 1], opacity: [0.25, 0, 0.25] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="absolute rounded-full pointer-events-none"
                        style={{ width: target.size + 16, height: target.size + 16, top: -8, left: -8, border: `1px solid ${target.color}25` }}
                      />
                      {/* Target body */}
                      <div
                        className="rounded-full border-2 flex items-center justify-center transition-all group-hover:scale-110 relative"
                        style={{
                          width: target.size,
                          height: target.size,
                          borderColor: target.color,
                          background: `radial-gradient(circle at 50% 40%, ${target.color}18 0%, ${target.color}06 70%)`,
                          boxShadow: `0 0 20px ${target.color}25, inset 0 0 10px ${target.color}08`,
                        }}
                      >
                        <TIcon size={target.size * 0.45} style={{ color: target.color, filter: `drop-shadow(0 0 4px ${target.color}60)` }} />
                        {/* Target crosshair lines */}
                        <div className="absolute inset-0 pointer-events-none opacity-20">
                          <div className="absolute top-0 bottom-0 left-1/2 w-px -translate-x-1/2" style={{ background: target.color }} />
                          <div className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2" style={{ background: target.color }} />
                        </div>
                      </div>
                      <span className="text-xs font-terminal tracking-wider px-1.5 py-0.5 rounded-sm bg-black/60 backdrop-blur-sm" style={{ color: target.color }}>
                        {target.label} +{target.points}
                      </span>
                    </motion.div>
                  );
                })}
              </AnimatePresence>

              {/* Hit markers */}
              <AnimatePresence>
                {hits.map(hit => (
                  <motion.div
                    key={`hit-${hit.id}`}
                    initial={{ scale: 0.5, opacity: 1 }}
                    animate={{ scale: 2.5, opacity: 0, y: -40 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.7 }}
                    className="absolute pointer-events-none z-20 flex flex-col items-center"
                    style={{ left: `${hit.x}%`, top: `${hit.y}%` }}
                  >
                    <span className="font-orbitron text-xl font-bold text-yellow-400" style={{ textShadow: '0 0 10px rgba(234,179,8,0.6)' }}>+{hit.points}</span>
                  </motion.div>
                ))}
              </AnimatePresence>

              {/* Bottom HUD */}
              <div className="absolute bottom-2 left-3 z-20 flex items-center gap-2 pointer-events-none">
                <Activity size={10} className="text-green-700/60" />
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: TOTAL_SPAWNS }).map((_, i) => (
                    <div key={i} className={`w-1 rounded-sm transition-colors ${i < spawned ? 'bg-red-800/30 h-2' : 'bg-green-700/30 h-3'}`} />
                  ))}
                </div>
              </div>
              <div className="absolute bottom-2 right-3 z-20 pointer-events-none">
                <span className="text-xs font-terminal text-green-800/50 tracking-wider">{currentTime}</span>
              </div>
            </div>

            {/* Legend */}
            <div className="flex justify-center gap-3 flex-wrap mt-3 max-w-2xl mx-auto">
              {TARGET_TYPES.map(t => (
                <div key={t.type} className="flex items-center gap-1.5 bg-black/25 px-2 py-1 rounded border border-gray-800/25">
                  <t.Icon size={12} style={{ color: t.color }} />
                  <span className="text-xs font-terminal tracking-wider" style={{ color: `${t.color}cc` }}>{t.label}</span>
                  <span className="text-xs font-orbitron text-yellow-500/80">+{t.points}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Success screen */}
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center space-y-4 mt-6 relative z-10 py-8"
          >
            {/* Green flash */}
            {successPhase >= 1 && (
              <motion.div
                className="fixed inset-0 bg-green-500/6 pointer-events-none z-50"
                initial={{ opacity: 1 }}
                animate={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              />
            )}
            {/* Shockwave ring */}
            {successPhase >= 1 && (
              <motion.div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 border border-green-500/20 rounded-full pointer-events-none"
                initial={{ width: 0, height: 0, opacity: 0.5 }}
                animate={{ width: 280, height: 280, opacity: 0 }}
                transition={{ duration: 0.8 }}
              />
            )}

            {/* Icon */}
            {successPhase >= 2 && (
              <div className="relative w-20 h-20 mx-auto">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                  className="absolute inset-0 border-2 border-dashed border-green-500/15 rounded-full"
                />
                <motion.div
                  className="absolute inset-0 flex items-center justify-center"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                >
                  <div className="w-16 h-16 rounded-full bg-green-900/25 border-2 border-green-500/70 flex items-center justify-center"
                    style={{ boxShadow: '0 0 25px rgba(0,255,65,0.15), inset 0 0 10px rgba(0,255,65,0.05)' }}>
                    <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                      <Crosshair size={28} className="text-green-400" />
                    </motion.div>
                  </div>
                </motion.div>
              </div>
            )}

            {/* Text reveals */}
            {successPhase >= 3 && (
              <>
                <motion.p
                  initial={{ opacity: 0, letterSpacing: '2px' }}
                  animate={{ opacity: 1, letterSpacing: '6px' }}
                  transition={{ duration: 0.5 }}
                  className="font-orbitron text-green-400 text-sm"
                  style={{ textShadow: '0 0 10px rgba(0,255,65,0.3)' }}
                >
                  {SHOOTING_GALLERY.completion.heading}
                </motion.p>
                <motion.p
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                  className="font-terminal text-xs text-green-600/60 tracking-wider"
                >
                  SCORE: {score}/{SCORE_GOAL}
                </motion.p>
                <motion.p
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.4 }}
                  className="text-gray-400/80 font-rajdhani"
                  style={{ textShadow: '0 0 6px rgba(234,179,8,0.15)' }}
                >
                  {SHOOTING_GALLERY.completion.itemText}
                </motion.p>
              </>
            )}
          </motion.div>
        )}

        {showProceed && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20 }}
            className="text-center mt-8 relative z-20"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onComplete?.()}
              className="inline-flex items-center gap-2 px-8 py-3 rounded font-orbitron text-sm tracking-widest text-black relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #22c55e, #00ff41)' }}
            >
              <span className="relative z-10">{SHOOTING_GALLERY.proceedButton}</span>
              <motion.div
                className="absolute inset-0"
                animate={{ x: ['-100%', '200%'] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)', width: '50%' }}
              />
            </motion.button>
          </motion.div>
        )}
      </motion.div>

      {/* Leon dialogues */}
      {showLeon && (
        <LeonDialogue
          message={SHOOTING_GALLERY.leon.intro}
          onDismiss={handleLeonDismiss}
        />
      )}

      {showFinalLeon && (
        <LeonDialogue
          message={SHOOTING_GALLERY.leon.complete.replace('{score}', score)}
          onDismiss={handleFinalDismiss}
        />
      )}
    </div>
  );
}
